/** The Profile page's "Larger text" preference (profile.html → Preferences).
 *
 *  Stored in localStorage rather than on FieldAdminProfile because it is a device display
 *  preference, like the OS text size: it has to apply on the first paint of a cold load, and
 *  a value that arrives with the profile query would leave every page briefly at the wrong
 *  size and then reflow. It is also genuinely per-device — the same person on a phone in a
 *  van and a tablet at the depot may well want different answers.
 *
 *  The design's other three toggles (push, SMS, daily summary) are server-side by nature,
 *  since a server has to act on them. If those are ever built, this one can move to join them.
 *
 *  SCALE is applied to the root element, so every `rem` in the Field Admin feature scales
 *  with it — 550 of the 563 fontSize declarations there are rem, and the remaining px values
 *  are padding, radii and icon boxes, which should NOT grow. Larger text, not a bigger UI.
 */

export const STORAGE_KEY = "fa.largerText";
export const SCALE = "18px";

const EVENT = "fa:larger-text";

export function readLargerText() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch (e) {
    // Safari in private mode throws on localStorage access rather than returning null.
    // A display preference is not worth failing a page render over.
    return false;
  }
}

export function writeLargerText(enabled) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch (e) {
    /* see readLargerText */
  }
  // localStorage's own `storage` event fires in OTHER tabs only, so the tab holding the
  // toggle needs telling explicitly or the change would not show until it navigated.
  window.dispatchEvent(new CustomEvent(EVENT, { detail: enabled }));
}

/** Subscribe to changes from this tab (custom event) and from others (storage event). */
export function subscribeLargerText(onChange) {
  const fromThisTab = (e) => onChange(e.detail);
  const fromOtherTab = (e) => { if (e.key === STORAGE_KEY) onChange(e.newValue === "true"); };
  window.addEventListener(EVENT, fromThisTab);
  window.addEventListener("storage", fromOtherTab);
  return () => {
    window.removeEventListener(EVENT, fromThisTab);
    window.removeEventListener("storage", fromOtherTab);
  };
}
